import { API_SECRET, buildApiUrl } from "../config/appConfig";
import { getDeviceId } from "./deviceStorage";
import { traceAndFetch } from "../sync/apiTrace"; // v3.2.08

export const CLIMATE_DATA_URL = buildApiUrl("climate-data.php");

const CLIMATE_API_CACHE_PREFIX = "fz:climate-api-cache:";
const climateInFlight = new Map();
let climateBackoffUntil = 0;

function climateCacheKey(body = {}) {
  return `${CLIMATE_API_CACHE_PREFIX}${String(body?.action || "unknown")}:${String(body?.farm_id || body?.station_uid || "global")}`;
}

function readClimateCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.data || null;
  } catch (e) { return null; }
}

function writeClimateCache(key, data) {
  try { localStorage.setItem(key, JSON.stringify({ at: Date.now(), data })); } catch (e) {}
}

async function climateApiCall(body, userName = "") {
  const safeBody = body || {};
  const key = climateCacheKey(safeBody);
  const signature = JSON.stringify({ body: safeBody, userName: userName || "system" });
  const cached = readClimateCache(key);
  if (Date.now() < climateBackoffUntil && cached) return cached;
  if (climateInFlight.has(signature)) return climateInFlight.get(signature);
  const task = (async () => {
    try {
      const resp = await traceAndFetch(CLIMATE_DATA_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": API_SECRET,
          "X-Device-ID": getDeviceId(),
          "X-User-Name": userName || "system",
        },
        body: JSON.stringify(safeBody),
      }, { source: "climate-data", action: String(safeBody?.action || "") });
      if (resp?.status === 429 || resp?.status === 503) {
        climateBackoffUntil = Date.now() + 3 * 60 * 1000;
        if (cached) return cached;
      }
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || json?.ok === false) throw new Error(json?.error || "Erro ao consultar clima");
      const data = json?.data || json;
      writeClimateCache(key, data);
      return data;
    } catch (e) {
      if (cached) return cached;
      throw e;
    } finally {
      climateInFlight.delete(signature);
    }
  })();
  climateInFlight.set(signature, task);
  return task;
}



export async function fetchClimateGlobalSummary({ preferredSource = "wunderground", fallbackToInmet = true, refreshHours = 1, retentionYears = 3, stationUid = "", userName = "" } = {}) {
  return climateApiCall({
    action: "global_summary",
    preferred_source: preferredSource,
    fallback_to_inmet: !!fallbackToInmet,
    refresh_hours: Number.isFinite(Number(refreshHours)) ? Number(refreshHours) : 1,
    retention_years: Number(retentionYears) || 3,
    station_uid: stationUid || undefined,
  }, userName);
}

export async function fetchFarmClimateSummary({ farmId, preferredSource = "wunderground", fallbackToInmet = true, latitude = null, longitude = null, stationCandidates = [], contextType = "", contextId = "", contextName = "", windowDays = 30, includeHistory = false, autoSync = true, userName = "" } = {}) {
  if (!farmId) return null;
  return climateApiCall({
    action: "summary",
    farm_id: farmId,
    preferred_source: preferredSource,
    fallback_to_inmet: !!fallbackToInmet,
    latitude: latitude == null || latitude === "" ? null : Number(latitude),
    longitude: longitude == null || longitude === "" ? null : Number(longitude),
    station_candidates: Array.isArray(stationCandidates) ? stationCandidates : [],
    context_type: contextType || undefined,
    context_id: contextId || undefined,
    context_name: contextName || undefined,
    window_days: Number(windowDays) || 30,
    include_history: !!includeHistory,
    auto_sync: !!autoSync,
  }, userName);
}


export async function fetchClimatePeriodSummaries({ farmId, preferredSource = "wunderground", fallbackToInmet = true, latitude = null, longitude = null, stationCandidates = [], autoSync = true, userName = "" } = {}) {
  if (!farmId) return { periods: {} };
  return climateApiCall({
    action: "period_summaries",
    farm_id: farmId,
    preferred_source: preferredSource,
    fallback_to_inmet: !!fallbackToInmet,
    latitude: latitude == null || latitude === "" ? null : Number(latitude),
    longitude: longitude == null || longitude === "" ? null : Number(longitude),
    station_candidates: Array.isArray(stationCandidates) ? stationCandidates : [],
    auto_sync: !!autoSync,
  }, userName);
}


export async function fetchClimateForecastBase({ farmId, preferredSource = "wunderground", fallbackToInmet = true, latitude = null, longitude = null, stationCandidates = [], autoSync = true, userName = "" } = {}) {
  if (!farmId) return { today: null, days: [], status: { key: "empty" } };
  return climateApiCall({
    action: "forecast_base",
    farm_id: farmId,
    preferred_source: preferredSource,
    fallback_to_inmet: !!fallbackToInmet,
    latitude: latitude == null || latitude === "" ? null : Number(latitude),
    longitude: longitude == null || longitude === "" ? null : Number(longitude),
    station_candidates: Array.isArray(stationCandidates) ? stationCandidates : [],
    auto_sync: !!autoSync,
  }, userName);
}

export async function fetchClimateAlertsBase({ farmId, preferredSource = "wunderground", fallbackToInmet = true, latitude = null, longitude = null, stationCandidates = [], alertRules = null, autoSync = true, userName = "" } = {}) {
  if (!farmId) return { status: { key: "pending" }, active_alerts: [], recent_alerts: [] };
  return climateApiCall({
    action: "alerts_base",
    farm_id: farmId,
    preferred_source: preferredSource,
    fallback_to_inmet: !!fallbackToInmet,
    latitude: latitude == null || latitude === "" ? null : Number(latitude),
    longitude: longitude == null || longitude === "" ? null : Number(longitude),
    station_candidates: Array.isArray(stationCandidates) ? stationCandidates : [],
    alert_rules: alertRules && typeof alertRules === "object" ? alertRules : undefined,
    auto_sync: !!autoSync,
  }, userName);
}


export async function fetchClimateDailyHistory({ farmId, limit = 90, userName = "" } = {}) {
  if (!farmId) return [];
  const data = await climateApiCall({
    action: "history",
    farm_id: farmId,
    limit: Number(limit) || 90,
  }, userName);
  return Array.isArray(data?.items) ? data.items : [];
}

export async function fetchClimateCalculationHistory({ farmId, contextType = "farm", contextId = "", limit = 10, userName = "" } = {}) {
  if (!farmId) return [];
  const data = await climateApiCall({
    action: "calculation_history",
    farm_id: farmId,
    context_type: contextType,
    context_id: contextId,
    limit: Number(limit) || 10,
  }, userName);
  return Array.isArray(data?.items) ? data.items : [];
}

export async function fetchClimateStations({ farmId, userName = "" } = {}) {
  if (!farmId) return [];
  const data = await climateApiCall({ action: "stations_list", farm_id: farmId }, userName);
  return Array.isArray(data?.items) ? data.items : [];
}

export async function saveClimateStation({ farmId, station = {}, userName = "" } = {}) {
  if (!farmId) throw new Error("farmId obrigatório");
  return climateApiCall({
    action: "station_upsert",
    farm_id: farmId,
    station,
  }, userName);
}

export async function deleteClimateStationRemote({ farmId, stationUid, userName = "" } = {}) {
  if (!farmId || !stationUid) throw new Error("farmId e stationUid obrigatórios");
  return climateApiCall({
    action: "station_delete",
    farm_id: farmId,
    station_uid: stationUid,
  }, userName);
}

export async function connectClimateStation({ farmId, station = {}, userName = "" } = {}) {
  if (!farmId) throw new Error("farmId obrigatório");
  return climateApiCall({
    action: "station_connect",
    farm_id: farmId,
    station,
  }, userName);
}


export async function syncFarmClimateStations({ farmId, preferredSource = "wunderground", fallbackToInmet = true, refreshHours = 6, latitude = null, longitude = null, stationCandidates = [], force = true, userName = "" } = {}) {
  if (!farmId) throw new Error("farmId obrigatório");
  return climateApiCall({
    action: "sync_auto",
    farm_id: farmId,
    preferred_source: preferredSource,
    fallback_to_inmet: !!fallbackToInmet,
    refresh_hours: Number(refreshHours) || 6,
    latitude: latitude == null || latitude === "" ? null : Number(latitude),
    longitude: longitude == null || longitude === "" ? null : Number(longitude),
    station_candidates: Array.isArray(stationCandidates) ? stationCandidates : [],
    force: !!force,
  }, userName);
}


export async function fetchClimateCronSettings({ userName = "" } = {}) {
  const data = await climateApiCall({ action: "cron_settings_get" }, userName);
  return data?.settings || null;
}

export async function saveClimateCronSettings({ settings = {}, userName = "" } = {}) {
  const data = await climateApiCall({ action: "cron_settings_save", settings }, userName);
  return data?.settings || null;
}

export async function rewriteClimateCronConfig({ userName = "" } = {}) {
  const data = await climateApiCall({ action: "cron_settings_rewrite_config" }, userName);
  return data?.settings || null;
}


export async function runClimateCronNow({ farmId = "", force = true, userName = "" } = {}) {
  return climateApiCall({
    action: "cron_run",
    farm_id: farmId || undefined,
    force: !!force,
  }, userName);
}
